import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentDashboard = ({ onNavigate, userInfo }) => {
  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    cgpa: "0.00",
    attendancePercentage: 0,
    hasPendingFees: false,
    feeAmount: "0",
    semesterName: "...",
    dueDate: "...",
    profileImage: null
  });
  const [attendanceData, setAttendanceData] = useState([]);
  const [marksData, setMarksData] = useState([]);
  const [subjectWiseMarks, setSubjectWiseMarks] = useState([]);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);
  const [universityStats, setUniversityStats] = useState({
    departments: 0,
    placementRate: 0
  });
  const [timetable, setTimetable] = useState([]);
  const [timetableData, setTimetableData] = useState(null);
  const [subjectAttendance, setSubjectAttendance] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  // Fetch Dashboard Data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch dashboard data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        console.log('🔑 Token found:', token ? 'Yes' : 'No');

        if (!token) {
          console.log('❌ No token found, cannot fetch data');
          setError('Authentication required. Please login again.');
          setLoading(false);
          return;
        }

        // Construct the full API URL
        const apiUrl = `${API_LINK}/api/student/dashboard`;
        console.log('🌐 API URL:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            handleLogout();
            return;
          } else if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('❌ Response is not JSON:', contentType);
          setError('Server returned non-JSON response.');
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('📦 Full API Response:', result);

        if (result.success && result.data) {
          const data = result.data;
          console.log('✅ Processing API data...');
          console.log('🎓 Academic data:', data.academic);
          console.log('📊 Recent marks:', data.academic?.recentMarks);
          console.log('📋 Subject-wise marks:', data.academic?.subjectWiseMarks);

          // Update student data
          setStudentData({
            name: data.student?.name || "Student",
            studentId: data.student?.studentId || "N/A",
            cgpa: data.student?.cgpa || data.academic?.cgpa || "0.00",
            attendancePercentage: data.student?.attendancePercentage || data.attendance?.overall || 0,
            hasPendingFees: data.fees?.hasPendingFees || false,
            feeAmount: data.fees?.pendingAmount || "0",
            semesterName: data.fees?.semester || "Current Semester",
            dueDate: data.fees?.dueDate || "N/A",
            profileImage: data.student?.profileImage || null
          });

          // Update marks data - only use API data
          setMarksData(data.academic?.recentMarks || []);
          setSubjectWiseMarks(data.academic?.subjectWiseMarks || []);

          // Update other data
          setAttendanceData(data.attendance?.recentRecords || []);
          setNotices(data.notices || []);
          setEvents(data.events || []);

          // Update university stats if available
          setUniversityStats({
            departments: data.university?.departments || 0,
            placementRate: data.university?.placementRate || 0
          });

          // Update timetable if available
          setTimetable(data.timetable || []);

          console.log('✅ Dashboard data updated successfully');
        } else {
          console.log('❌ API returned error:', result.message);
          setError(result.message || 'Failed to fetch dashboard data');
        }

      } catch (error) {
        console.error('💥 Error fetching dashboard data:', error);
        setError(`Failed to load dashboard data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchDashboardData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Fetch Student Marks from new endpoint
  useEffect(() => {
    const fetchStudentMarks = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;

        console.log('🔍 Fetching student marks from /api/student/marks...');
        const apiUrl = `${API_LINK}/api/student/marks`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('📊 Marks API Response:', result);

          if (result.success && result.data && result.data.marks) {
            // Map the marks data to match the expected format
            const formattedMarks = result.data.marks.map(mark => ({
              subjectName: mark.subject_name,
              subjectCode: mark.subject_code,
              internal1: mark.internal1_marks,
              internal2: mark.internal2_marks,
              semesterMarks: mark.semester_marks,
              totalMarks: mark.total_marks,
              grade: mark.grade,
              semester: mark.semester,
              credits: mark.credits
            }));

            console.log('✅ Formatted marks:', formattedMarks);
            setSubjectWiseMarks(formattedMarks);
          }
        }
      } catch (error) {
        console.error('💥 Error fetching student marks:', error);
      }
    };

    fetchStudentMarks();
  }, []);

  // Fetch Student Timetable
  useEffect(() => {
    const fetchTimetable = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;

        console.log('🔍 Fetching student timetable...');
        const apiUrl = `${API_LINK}/api/student/timetable`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('📅 Timetable API Response:', result);

          if (result.success && result.data) {
            setTimetableData(result.data);
          }
        }
      } catch (error) {
        console.error('💥 Error fetching timetable:', error);
      }
    };

    fetchTimetable();
  }, []);

  // Fetch Subject-wise Attendance
  useEffect(() => {
    const fetchSubjectAttendance = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;

        console.log('🔍 Fetching subject-wise attendance...');
        const apiUrl = `${API_LINK}/api/student/subject-attendance`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('📚 Subject Attendance API Response:', result);

          if (result.success && result.data && result.data.subjects) {
            setSubjectAttendance(result.data.subjects);
          }
        }
      } catch (error) {
        console.error('💥 Error fetching subject attendance:', error);
      }
    };

    fetchSubjectAttendance();
  }, []);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation for dashboard buttons
  const handleNavigation = (page) => {
    if (onNavigate) {
      switch (page) {
        case 'Marks':
          onNavigate('student-marks');
          break;
        case 'View Full Attendance':
          onNavigate('student-attendance');
          break;
        default:
          console.log(`Navigation to ${page} clicked`);
      }
    }
  };




  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get grade color
  const getGradeColor = (grade) => {
    const gradeColors = {
      'A+': '#2e7d32', 'A': '#388e3c', 'B+': '#689f38', 'B': '#7cb342',
      'C+': '#f57c00', 'C': '#ff9800', 'D': '#f44336', 'F': '#d32f2f'
    };
    return gradeColors[grade] || '#666';
  };

  // Helper function to get marks color based on performance
  const getMarksColor = (marks, maxMarks) => {
    if (!marks || marks === 0) return '#f5f5f5';
    const percentage = (marks / maxMarks) * 100;
    if (percentage >= 90) return '#4caf50';
    if (percentage >= 80) return '#8bc34a';
    if (percentage >= 70) return '#ffeb3b';
    if (percentage >= 60) return '#ff9800';
    if (percentage >= 50) return '#ff5722';
    return '#f44336';
  };

  // Table styles
  const tableHeaderStyle = {
    padding: '1rem 1rem',
    textAlign: 'center',
    fontWeight: 700,
    color: '#ffffff',
    border: '2px solid #238548',
    fontSize: '0.95rem',
    backgroundColor: '#238548',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const tableCellStyle = {
    padding: '1rem',
    border: '1px solid #c0c0c0',
    fontSize: '0.9rem',
    verticalAlign: 'middle',
    textAlign: 'center',
    backgroundColor: '#ffffff'
  };

  const marksStyle = {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#ffffff',
    minWidth: '35px',
    display: 'inline-block',
    textAlign: 'center'
  };

  // Debug logging
  console.log('🖥️ Rendering dashboard with:', {
    marksDataLength: marksData.length,
    subjectWiseMarksLength: subjectWiseMarks.length,
    loading: loading,
    error: error
  });

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Dashboard...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '3rem', color: '#f44336' }}>⚠️</div>
        <p style={{ color: '#666', fontSize: '1.1rem', textAlign: 'center', maxWidth: '400px' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#238548',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-dashboard"
      pageTitle="Dashboard"
    >
      {/* Fee Notification Bar */}
      <div style={{
        marginBottom: '20px',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#238548'
      }}>
        <div style={{
          width: '100%',
          height: '40px',
          overflow: 'hidden',
          backgroundColor: '#238548'
        }}>
          <div style={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            animation: 'marquee 15s linear infinite'
          }}>
            <span style={{ marginRight: '10px' }}>🔔</span>
            {studentData.hasPendingFees
              ? `Hello ${studentData.name}! You have pending fees of ₹${studentData.feeAmount} for ${studentData.semesterName}. Due date: ${formatDate(studentData.dueDate)}. Please pay as soon as possible.`
              : `Hello ${studentData.name}! All your fees have been paid. No pending payments.`
            }
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Attendance Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>✅</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Attendance</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {studentData.attendancePercentage > 0 ? `${studentData.attendancePercentage}%` : 'N/A'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            {studentData.attendancePercentage >= 75 ? 'Good Standing' :
              studentData.attendancePercentage > 0 ? 'Needs Improvement' : 'No data available'}
          </p>
        </div>

        {/* CGPA Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>🏆</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>CGPA</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {parseFloat(studentData.cgpa) > 0 ? studentData.cgpa : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            {parseFloat(studentData.cgpa || '0') >= 8.0 ? 'Excellent' :
              parseFloat(studentData.cgpa || '0') >= 6.0 ? 'Good' :
                parseFloat(studentData.cgpa || '0') > 0 ? 'Average' : 'Not Available'}
          </p>
        </div>

        {/* Departments Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>🏢</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Departments</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {universityStats.departments > 0 ? universityStats.departments : '22'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Active Departments
          </p>
        </div>

        {/* Placement Rate Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>💼</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Placement Rate</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {universityStats.placementRate > 0 ? `${universityStats.placementRate}%` : '95%'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Current Year
          </p>
        </div>
      </section>

      {/* Academic Performance Section - Recent Marks */}
      {marksData.length > 0 ? (
        <section style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#238548',
              color: '#ffffff',
              padding: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <span style={{ fontSize: '1.8rem' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Academic Performance</h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem'
              }}>
                {marksData.slice(0, 4).map((mark, index) => (
                  <div key={index} style={{
                    padding: '1rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <h4 style={{
                        margin: 0,
                        color: '#333',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}>
                        {mark.subject}
                      </h4>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        color: '#ffffff',
                        backgroundColor: getGradeColor(mark.grade)
                      }}>
                        {mark.grade}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.9rem',
                      color: '#666'
                    }}>
                      <span>Total: {mark.total || 'N/A'}</span>
                      <span>Sem: {mark.semester}</span>
                    </div>
                    {mark.internal1 && mark.internal2 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.8rem',
                        color: '#888',
                        marginTop: '0.5rem'
                      }}>
                        <span>I1: {mark.internal1}</span>
                        <span>I2: {mark.internal2}</span>
                        <span>Sem: {mark.semesterMarks}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#238548',
              color: '#ffffff',
              padding: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <span style={{ fontSize: '1.8rem' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Academic Performance</h3>
            </div>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}>📚</div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>No academic performance data available</p>
            </div>
          </div>
        </section>
      )}

      {/* Subject-wise Detailed Marks */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '1.8rem' }}>📋</span>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Subject-wise Marks Details</h3>
            </div>
            <button
              onClick={() => handleNavigation('Marks')}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
            >
              View All Marks
            </button>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {/* Table View - Always Visible */}
            <div style={{
              overflowX: 'auto',
              borderRadius: '8px',
              border: '2px solid #dee2e6',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: '#ffffff'
              }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Subject</th>
                    <th style={tableHeaderStyle}>Internal 1</th>
                    <th style={tableHeaderStyle}>Internal 2</th>
                    <th style={tableHeaderStyle}>Semester</th>
                    <th style={tableHeaderStyle}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectWiseMarks.length > 0 ? (
                    subjectWiseMarks.map((subject, index) => (
                      <tr key={index} style={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                        transition: 'all 0.3s ease'
                      }}>
                        <td style={{ ...tableCellStyle, textAlign: 'left', maxWidth: '200px' }}>
                          <div>
                            <div style={{ fontWeight: 600, color: '#333' }}>
                              {subject.subjectName}
                            </div>
                            {subject.subjectCode && (
                              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                                {subject.subjectCode}
                              </div>
                            )}
                          </div>
                        </td>

                        <td style={tableCellStyle}>
                          <span style={{
                            ...marksStyle,
                            backgroundColor: getMarksColor(subject.internal1, 30)
                          }}>
                            {subject.internal1 || '-'}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{
                            ...marksStyle,
                            backgroundColor: getMarksColor(subject.internal2, 30)
                          }}>
                            {subject.internal2 || '-'}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{
                            ...marksStyle,
                            backgroundColor: getMarksColor(subject.semesterMarks, 100)
                          }}>
                            {subject.semesterMarks || '-'}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{
                            fontWeight: 'bold',
                            color: '#238548',
                            fontSize: '1rem'
                          }}>
                            {subject.totalMarks || '-'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    // Show 5 empty placeholder rows when no data
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index} style={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                      }}>
                        <td style={{ ...tableCellStyle, textAlign: 'left', color: '#999' }}>-</td>
                        <td style={{ ...tableCellStyle, color: '#999' }}>-</td>
                        <td style={{ ...tableCellStyle, color: '#999' }}>-</td>
                        <td style={{ ...tableCellStyle, color: '#999' }}>-</td>
                        <td style={{ ...tableCellStyle, color: '#999' }}>-</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Attendance Summary Section */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '1.8rem' }}>✅</span>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Attendance Summary</h3>
            </div>
            <button
              onClick={() => handleNavigation('View Full Attendance')}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
            >
              View Full Attendance
            </button>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              marginBottom: '1.5rem',
              borderBottom: '1px solid #ecf0f1',
              paddingBottom: '1.5rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#238548'
                }}>{studentData.attendancePercentage > 0 ? `${studentData.attendancePercentage}%` : 'N/A'}</div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}>Overall</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#238548'
                }}>{attendanceData.filter(r => r.status === 'present').length || 0}</div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}>Present</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#238548'
                }}>{attendanceData.filter(r => r.status === 'absent').length || 0}</div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}>Absent</div>
              </div>
            </div>

            <h4 style={{
              margin: '1rem 0',
              color: '#333',
              fontSize: '1.2rem'
            }}>Subject-wise Attendance</h4>

            {/* Subject-wise Attendance Table */}
            <div style={{
              overflowX: 'auto',
              borderRadius: '8px',
              border: '2px solid #dee2e6',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: '#ffffff'
              }}>
                <thead>
                  <tr>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 700,
                      color: '#ffffff',
                      border: '2px solid #238548',
                      fontSize: '0.9rem',
                      backgroundColor: '#238548',
                      textTransform: 'uppercase'
                    }}>Subject</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 700,
                      color: '#ffffff',
                      border: '2px solid #238548',
                      fontSize: '0.9rem',
                      backgroundColor: '#238548',
                      textTransform: 'uppercase'
                    }}>Total Classes</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 700,
                      color: '#ffffff',
                      border: '2px solid #238548',
                      fontSize: '0.9rem',
                      backgroundColor: '#238548',
                      textTransform: 'uppercase'
                    }}>Attended</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 700,
                      color: '#ffffff',
                      border: '2px solid #238548',
                      fontSize: '0.9rem',
                      backgroundColor: '#238548',
                      textTransform: 'uppercase'
                    }}>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectAttendance.length > 0 ? (
                    subjectAttendance.map((subject, index) => (
                      <tr key={index} style={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                      }}>
                        <td style={{
                          padding: '1rem',
                          border: '1px solid #c0c0c0',
                          fontSize: '0.9rem',
                          verticalAlign: 'middle',
                          textAlign: 'left'
                        }}>
                          <div style={{ fontWeight: 600, color: '#333' }}>
                            {subject.subjectName}
                          </div>
                          {subject.subjectCode && (
                            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                              {subject.subjectCode}
                            </div>
                          )}
                        </td>
                        <td style={{
                          padding: '1rem',
                          border: '1px solid #c0c0c0',
                          fontSize: '0.9rem',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          fontWeight: 500
                        }}>
                          {subject.totalClasses}
                        </td>
                        <td style={{
                          padding: '1rem',
                          border: '1px solid #c0c0c0',
                          fontSize: '0.9rem',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          fontWeight: 500
                        }}>
                          {subject.attended}
                        </td>
                        <td style={{
                          padding: '1rem',
                          border: '1px solid #c0c0c0',
                          fontSize: '0.9rem',
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: '#ffffff',
                            backgroundColor: subject.percentage >= 75 ? '#4caf50' :
                              subject.percentage >= 60 ? '#ff9800' :
                                subject.percentage > 0 ? '#f44336' : '#9e9e9e'
                          }}>
                            {subject.percentage.toFixed(2)}%
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '1rem'
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📚</div>
                        <div>No subjects found for your batch</div>
                        <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#999' }}>
                          Subjects will appear here once they are added for your department and year
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Class Timetable Section */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📅</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Class Timetable</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{
              overflowX: 'auto',
              borderRadius: '8px',
              border: '2px solid #dee2e6',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: '#ffffff'
              }}>
                <thead>
                  <tr>
                    <th style={{ ...tableHeaderStyle, minWidth: '120px' }}>Time Period</th>
                    <th style={tableHeaderStyle}>Monday</th>
                    <th style={tableHeaderStyle}>Tuesday</th>
                    <th style={tableHeaderStyle}>Wednesday</th>
                    <th style={tableHeaderStyle}>Thursday</th>
                    <th style={tableHeaderStyle}>Friday</th>
                    <th style={tableHeaderStyle}>Saturday</th>
                  </tr>
                </thead>
                <tbody>
                  {timetableData && timetableData.periods && timetableData.periods.filter(p => !p.isBreak).length > 0 ? (
                    timetableData.periods.filter(p => !p.isBreak).map((period) => {
                      const formatTime = (time) => {
                        if (!time) return '';
                        const [hours, minutes] = time.split(':');
                        const hour = parseInt(hours);
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const hour12 = hour % 12 || 12;
                        return `${hour12}:${minutes} ${ampm}`;
                      };

                      const getClassForDay = (day) => {
                        const schedule = timetableData.schedule || {};
                        const daySchedule = schedule[day.toLowerCase()] || [];
                        return daySchedule.find(entry => entry.periodId === period.id);
                      };

                      return (
                        <tr key={period.id}>
                          <td style={{
                            ...tableCellStyle,
                            fontWeight: 600,
                            backgroundColor: '#f8f9fa',
                            textAlign: 'left',
                            padding: '1rem',
                            minHeight: '60px'
                          }}>
                            <div style={{ fontSize: '0.85rem', color: '#333' }}>{period.periodName}</div>
                            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                              {formatTime(period.startTime)} - {formatTime(period.endTime)}
                            </div>
                          </td>
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => {
                            const classEntry = getClassForDay(day);
                            return (
                              <td key={day} style={{ ...tableCellStyle, padding: '0.5rem', minHeight: '60px', verticalAlign: 'top' }}>
                                {classEntry ? (
                                  <div style={{ fontSize: '0.75rem' }}>
                                    <div style={{ fontWeight: 600, color: '#238548', marginBottom: '0.25rem' }}>
                                      {classEntry.subjectName}
                                    </div>
                                    <div style={{ color: '#666', fontSize: '0.7rem' }}>
                                      {classEntry.teacherName}
                                    </div>
                                    {classEntry.roomNumber && (
                                      <div style={{ color: '#999', fontSize: '0.65rem', marginTop: '0.15rem' }}>
                                        📍 {classEntry.roomNumber}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  ) : (
                    // Show empty rows when no data
                    [
                      { period: 'Period 1', time: '9:11 AM - 11:00 AM' },
                      { period: 'Period 2', time: '11:00 AM - 12:00 PM' },
                      { period: 'Period 3', time: '12:00 PM - 1:00 PM' },
                      { period: 'Period 4', time: '2:00 PM - 3:00 PM' },
                      { period: 'Period 5', time: '3:00 PM - 4:00 PM' }
                    ].map((periodInfo, index) => (
                      <tr key={index}>
                        <td style={{
                          ...tableCellStyle,
                          fontWeight: 600,
                          backgroundColor: '#f8f9fa',
                          textAlign: 'left',
                          padding: '1rem',
                          minHeight: '60px'
                        }}>
                          <div style={{ fontSize: '0.85rem', color: '#333' }}>{periodInfo.period}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                            {periodInfo.time}
                          </div>
                        </td>
                        <td style={{ ...tableCellStyle, padding: '1rem', minHeight: '60px' }}></td>
                        <td style={{ ...tableCellStyle, padding: '1rem', minHeight: '60px' }}></td>
                        <td style={{ ...tableCellStyle, padding: '1rem', minHeight: '60px' }}></td>
                        <td style={{ ...tableCellStyle, padding: '1rem', minHeight: '60px' }}></td>
                        <td style={{ ...tableCellStyle, padding: '1rem', minHeight: '60px' }}></td>
                        <td style={{ ...tableCellStyle, padding: '1rem', minHeight: '60px' }}></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>


      {/* Important Notices & Upcoming Events */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem'
      }}>
        {/* Important Notices */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📋</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Important Notices</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {notices.length > 0 ? notices.slice(0, 3).map((notice, index) => (
              <div key={index} style={{
                padding: '1rem',
                borderBottom: '1px solid #ecf0f1'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{notice.title}</h4>
                <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.9rem' }}>{notice.content}</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#999' }}>{formatDate(notice.date)}</p>
              </div>
            )) : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}>📋</div>
                <p style={{ color: '#666', fontSize: '1.1rem' }}>No notices available</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📅</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Upcoming Events</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {events.length > 0 ? events.slice(0, 3).map((event, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '1.5rem',
                padding: '1.5rem',
                borderBottom: '1px solid #ecf0f1'
              }}>
                <div style={{
                  background: '#238548',
                  color: '#ffffff',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  minWidth: '80px'
                }}>
                  <div style={{ fontWeight: 'bold' }}>{new Date(event.date).getDate()}</div>
                  <div style={{ fontSize: '0.8rem' }}>
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{event.title}</h4>
                  <p style={{
                    margin: '0 0 0.5rem 0',
                    color: '#666',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <span>🕐 {event.time}</span>
                    <span>📍 {event.location}</span>
                  </p>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    background: '#f4f7fa',
                    color: '#666',
                    borderRadius: '15px',
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}>
                    {event.type}
                  </span>
                </div>
              </div>
            )) : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}>📅</div>
                <p style={{ color: '#666', fontSize: '1.1rem' }}>No upcoming events</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default StudentDashboard;